'use client';

import { type BoxProps } from '@ironhack/design-system2/components/box';
import { Button } from '@ironhack/design-system2/components/button';
import { Flex } from '@ironhack/design-system2/components/flex';
import { FormFeedback } from '@ironhack/design-system2/components/form-feedback';
import { Text } from '@ironhack/design-system2/components/text';
import { zodResolver } from '@hookform/resolvers/zod';
import { omit } from 'ramda';
import { type ReactElement, useState } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import { useSessionStorage } from 'react-use';
import { type Entries } from 'type-fest';
import { type StructuredTextGraphQlResponse } from 'react-datocms';

import { CheckboxField } from '@/app/components/client/checkbox-field';
import { FormThankYou } from '@/app/components/client/form-thank-you';
import { ModuleWrapper } from '@/app/components/client/module-wrapper';
import { PhoneField } from '@/app/components/client/phone-field';
import { SelectField } from '@/app/components/client/select-field';
import { TextareaField } from '@/app/components/client/textarea-field';
import { TextField } from '@/app/components/client/text-field';
import { useFormRequest } from '@/hooks';
import { sendEvent } from '@/lib/gtm';
import { TurnstileFormToken } from '@/lib/turnstile/components';
import { regionCodesToEnglishNames, regionToMainCountry } from '@/lib/utils';
import { type DatoButtonBlock } from '@/lib/datocms/types';
import { type ValidationMessageSet } from '@/lib/utils/form-validations';
import {
  formReferrerMap,
  getB2bSchema,
} from '@/lib/utils/form-validations/forms/b2b-form';
import { type EmailInput } from '@/lib/utils/form-validations/schemas/email-schema';
import {
  type NameInputs,
  type NameValidationMessages,
} from '@/lib/utils/form-validations/schemas/name-schema';
import {
  type PhoneInputs,
  getPhoneDefaults,
} from '@/lib/utils/form-validations/schemas/phone-schema';
import { type Language } from '@/lib/utils/language';
import { type Region } from '@/types';

type SalesforceRole =
  | 'Business Analyst'
  | 'C-Level'
  | 'Data Analyst'
  | 'Founder/Cofounder'
  | 'HR Director'
  | 'HR Generalist'
  | 'HR Manager'
  | 'IT/Product Manager'
  | 'Marketing/Communications Manager'
  | 'Partnerships Director/Manager'
  | 'Procurement Director/Manager'
  | 'Project/Operations Manager'
  | 'Sales Director'
  | 'Sales/Business Development Manager'
  | 'Software/Web Developer'
  | 'Talent Acquisition Director'
  | 'Talent Acquisition Manager'
  | 'Talent Acquisition Specialist'
  | 'Training and Development Director'
  | 'Training and Development Manager'
  | 'Training and Development Specialist'
  | 'UX/UI Designer'
  | 'Other';

export type B2bForm = {
  companyLabel: string;
  companyPlaceholder: string;
  companyRequiredMessage: string;
  emailLabel: string;
  emailPlaceholder: string;
  emailValidationMessages: ValidationMessageSet;
  errorMessage: string;
  firstNameLabel: string;
  firstNamePlaceholder: string;
  firstNameValidationMessages: NameValidationMessages;
  hiringLocationLabel: string;
  hiringLocationOtherOptionText: string;
  hiringLocationPlaceholder: string;
  hiringLocationRequiredMessage: string;
  jobPostingInvalidMessage: string;
  jobPostingLabel: string;
  jobPostingPlaceholder: string;
  lastNameLabel: string;
  lastNamePlaceholder: string;
  lastNameValidationMessages: NameValidationMessages;
  legalText: StructuredTextGraphQlResponse;
  legalTextRequiredMessage: string;
  messageLabel: string;
  messagePlaceholder: string;
  messageRequiredMessage: string;
  newsletterLabel: string;
  phoneInvalidMessage: string;
  phoneLabel: string;
  phonePlaceholder: string;
  phoneRequiredMessage: string;
  roleLabel: string;
  roleOptions: Array<{ name: string; salesforceValue: SalesforceRole }>;
  rolePlaceholder: string;
  roleRequiredMessage: string;
  submitButtonText: string;
  thankYouText: string;
  thankYouTitle: string;
  title: string;
};

type Props = {
  b2bForm: B2bForm;
  language: Language;
  region: Region;
  thankYouButton: [DatoButtonBlock];
} & BoxProps;

type FormData = {
  acceptedTerms: boolean;
  company: string;
  cToken: string;
  hiringLocation: string;
  jobPosting?: string;
  message: string;
  newsletter?: boolean;
  phone: PhoneInputs;
  role: SalesforceRole | '';
} & EmailInput &
  NameInputs;

const getValidRegionOptions = (language: Language) =>
  (
    Object.entries(regionCodesToEnglishNames) as Entries<
      typeof regionCodesToEnglishNames
    >
  )
    .filter(([code]) => code !== 'ww')
    .map(([code, englishName]) => ({
      key: englishName,
      text: new Intl.DisplayNames(language, { type: 'region' }).of(
        regionToMainCountry(code).toUpperCase()
      ) as string, // cast to string to avoid the possible undefined return
    }));

export const B2bForm = (props: Props): ReactElement => {
  const { b2bForm, thankYouButton, language, region, ...boxProps } = props;
  const {
    companyLabel,
    companyPlaceholder,
    emailLabel,
    emailPlaceholder,
    errorMessage,
    firstNameLabel,
    firstNamePlaceholder,
    hiringLocationLabel,
    hiringLocationOtherOptionText,
    hiringLocationPlaceholder,
    jobPostingLabel,
    jobPostingPlaceholder,
    lastNameLabel,
    lastNamePlaceholder,
    legalText,
    messageLabel,
    messagePlaceholder,
    newsletterLabel,
    phoneLabel,
    phonePlaceholder,
    roleLabel,
    roleOptions = [],
    rolePlaceholder,
    submitButtonText,
    thankYouText,
    thankYouTitle,
    title,
  } = b2bForm;

  const [formReferrer] = useSessionStorage<string>('formReferrer', '', true);

  const formSchema = getB2bSchema(b2bForm);

  const formMethods = useForm<FormData>({
    defaultValues: {
      hiringLocation: region === 'ww' ? '' : regionCodesToEnglishNames[region],
      phone: getPhoneDefaults(region),
      role: '',
    },
    mode: 'onBlur',
    reValidateMode: 'onBlur',
    resolver: zodResolver(formSchema),
  });
  const {
    formState: { errors, isSubmitting },
    handleSubmit,
    setValue,
  } = formMethods;

  const [formFeedback, setFormFeedback] = useState<{
    status: 'error';
    message: string;
  } | null>();
  const [isSubmitSuccessful, setIsSubmitSuccessful] = useState<boolean>(false);
  const formRequest = useFormRequest();
  const setCaptchaToken = (token: string) => setValue('cToken', token);

  const onSubmit = async (formData: FormData) => {
    setFormFeedback(null);
    const { hiringLocation, jobPosting, newsletter, ...apiData } = formData;

    try {
      await formRequest({
        data: {
          ...omit(['acceptedTerms'], apiData),
          emailOptIn: Boolean(newsletter),
          interest:
            formReferrerMap[formReferrer as keyof typeof formReferrerMap] ??
            'General',
          jobPostUrl: jobPosting,
          language,
          location: hiringLocation,
        },
        formType: 'b2b',
      });
      setIsSubmitSuccessful(true);

      const { company } = apiData;
      sendEvent({
        eventAction: 'send b2b request',
        eventCategory: 'Companies',
        eventLabel: `${company}::${hiringLocation}`,
        pageType: 'Companies',
      });
    } catch {
      setFormFeedback({ status: 'error', message: errorMessage });
    }
  };

  return (
    <ModuleWrapper
      innerProps={{ justify: 'center' }}
      moduleFormat={{
        background: 'transparent',
        spacingTop: '40',
        spacingBottom: '40',
      }}
      outerProps={boxProps}
    >
      {isSubmitSuccessful ? (
        <FormThankYou
          bg={[null, null, 'background.white']}
          borderRadius={[null, null, '2xl']}
          button={thankYouButton}
          color={[null, null, 'text.primary']}
          id="b2bSuccess"
          p={[0, null, 5]}
          text={thankYouText}
          title={thankYouTitle}
          w={['100%', null, '400px']}
        />
      ) : (
        <Flex
          bg={[null, null, 'background.white']}
          borderRadius={[null, null, '2xl']}
          color={[null, null, 'text.primary']}
          direction="column"
          gap={4}
          p={[0, null, 5]}
          w={['100%', null, '680px']}
        >
          <Text as="h1" textStyle={['3xl', null, '5xl']}>
            {title}
          </Text>

          <FormProvider {...formMethods}>
            <Flex
              as="form"
              direction="column"
              gap={3}
              noValidate
              onSubmit={handleSubmit(onSubmit)}
            >
              <Flex direction={['column', null, 'row']} gap={3} w="full">
                <TextField
                  error={errors.firstName?.message}
                  isRequired
                  label={firstNameLabel}
                  name="firstName"
                  placeholder={firstNamePlaceholder}
                  type="text"
                />
                <TextField
                  error={errors.lastName?.message}
                  isRequired
                  label={lastNameLabel}
                  name="lastName"
                  placeholder={lastNamePlaceholder}
                  type="text"
                />
              </Flex>
              <Flex direction={['column', null, 'row']} gap={3} w="full">
                <TextField
                  error={errors.company?.message}
                  isRequired
                  label={companyLabel}
                  name="company"
                  placeholder={companyPlaceholder}
                  type="text"
                />
                <SelectField
                  error={errors.hiringLocation?.message}
                  isRequired
                  label={hiringLocationLabel}
                  name="hiringLocation"
                  options={[
                    ...getValidRegionOptions(language),
                    {
                      key: 'Other country',
                      text: hiringLocationOtherOptionText,
                    },
                  ]}
                  placeholder={hiringLocationPlaceholder}
                />
              </Flex>
              <Flex direction={['column', null, 'row']} gap={3} w="full">
                <TextField
                  error={errors.email?.message}
                  isRequired
                  label={emailLabel}
                  name="email"
                  placeholder={emailPlaceholder}
                  type="email"
                />
                <PhoneField
                  error={
                    errors.phone?.message || errors.phone?.phoneNumber?.message
                  }
                  isRequired
                  label={phoneLabel}
                  name="phone"
                  placeholder={phonePlaceholder}
                />
              </Flex>
              <SelectField
                error={errors.role?.message}
                isRequired
                label={roleLabel}
                name="role"
                options={roleOptions.map(({ name, salesforceValue }) => ({
                  key: salesforceValue,
                  text: name,
                }))}
                placeholder={rolePlaceholder}
              />
              <TextField
                error={errors.jobPosting?.message}
                isRequired={false}
                label={jobPostingLabel}
                name="jobPosting"
                placeholder={jobPostingPlaceholder}
                type="url"
              />
              <TextareaField
                error={errors.message?.message}
                isRequired
                label={messageLabel}
                name="message"
                placeholder={messagePlaceholder}
              />

              <Flex direction="column" gap={1} mt={-3}>
                <TurnstileFormToken
                  language={language}
                  onVerify={setCaptchaToken}
                />
                <CheckboxField
                  color={['text.inverse', null, 'text.primary']}
                  error={errors.acceptedTerms?.message}
                  isRequired
                  label={legalText}
                  lineHeight={0}
                  ml="1px"
                  name="acceptedTerms"
                  px={2}
                  py={1}
                  size="l"
                />

                <Flex direction="column" gap={2}>
                  <CheckboxField
                    color={['text.inverse', null, 'text.primary']}
                    isRequired={false}
                    label={newsletterLabel}
                    lineHeight={0}
                    ml="1px"
                    name="newsletter"
                    px={2}
                    py={1}
                    size="l"
                  />
                  <Button
                    fontSize="l"
                    isLoading={isSubmitting}
                    maxW="full"
                    size="lg"
                    type="submit"
                  >
                    {submitButtonText}
                  </Button>

                  {formFeedback?.status && <FormFeedback {...formFeedback} />}
                </Flex>
              </Flex>
            </Flex>
          </FormProvider>
        </Flex>
      )}
    </ModuleWrapper>
  );
};
